import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { ErrorState, LoadingButton, LoadingSpinner, PageHeader } from '@airclou/ui'
import { useToastActions } from '@airclou/ui'
import {
  CreditCard,
  Users,
  // TrendingUp, -- used by the hidden "View usage details" action
  Gauge,
  Package,
  UserPlus,
  ArrowUpRight,
} from 'lucide-react'
import { canManageOrg, canSeeBilling } from '../../navigation'
import {
  StatCard,
  QuickActions,
  ActivityFeed,
  AlertsPanel,
  UsageChart,
  StorageUsageCard,
} from '../../components/overview'
import {
  fetchOverviewStats,
  fetchRecentActivity,
  fetchAlerts,
  fetchUsageTrends,
  type OverviewStats,
  type ActivityItem,
  type Alert,
  type UsageTrendData,
} from '../../api/overview'

export function OverviewPage() {
  const { user, token } = useAuth()
  const navigate = useNavigate()
  const { error: showErrorToast } = useToastActions()
  const { t } = useTranslation('dashboard')

  // State management
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<OverviewStats | null>(null)
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [usageTrends, setUsageTrends] = useState<UsageTrendData[]>([])

  // Fetch all overview data
  useEffect(() => {
    async function loadOverviewData() {
      if (!token) return

      setLoading(true)
      setError(null)

      try {
        const [statsData, activitiesData, alertsData, trendsData] = await Promise.all([
          fetchOverviewStats(token),
          fetchRecentActivity(token, 8),
          fetchAlerts(token),
          fetchUsageTrends(token, 30),
        ])

        setStats(statsData)
        setActivities(activitiesData)
        setAlerts(alertsData)
        setUsageTrends(trendsData)
      } catch (err) {
        console.error('Failed to load overview data:', err)
        setError(err instanceof Error ? err.message : t('overview.failedToLoad'))
        showErrorToast(
          t('overview.unableToFetch'),
          t('overview.failedToLoadDashboard')
        )
      } finally {
        setLoading(false)
      }
    }

    loadOverviewData()
  }, [token, showErrorToast])

  // Seats reads "7 / 200", "7 / Unlimited", or just "7" when the tenant has no
  // plan and therefore no cap we can name. The three cases are deliberately
  // different: an uncapped plan and an unreadable one must not look alike.
  const seatsValue = !stats
    ? '—'
    : stats.usage.seats.limit === null
      ? `${stats.usage.seats.used}`
      : `${stats.usage.seats.used} / ${
          stats.usage.seats.limit === 'unlimited'
            ? t('overview.unlimited')
            : stats.usage.seats.limit
        }`

  // Quick actions, on the same role rule as the sidebar (see ../../navigation):
  // a shortcut to a page the sidebar hides is the same dead end, one click
  // further in. A viewer gets none, and the panel hides rather than sitting
  // empty.
  //
  // No "Manage Add-ons" action: there is no add-ons page. It used to point at
  // `/subscription/addons`, which redirected to a `/billing/addons` tab that
  // does not exist and bounced the operator back to the billing overview.
  const showBilling = canSeeBilling(user?.orgRole ?? undefined)
  const quickActions = [
    ...(showBilling
      ? [{
          label: t('overview.upgradePlan'),
          icon: ArrowUpRight,
          onClick: () => navigate('/billing/subscription'),
        }]
      : []),
    ...(canManageOrg(user?.orgRole ?? undefined)
      ? [{
          label: t('overview.inviteTeamMembers'),
          icon: UserPlus,
          onClick: () => navigate('/people'),
        }]
      : []),
    // "View usage details" hidden with the billing Usage tab (2026-10-05).
    // ...(showBilling
    //   ? [{
    //       label: t('overview.viewUsageDetails'),
    //       icon: TrendingUp,
    //       onClick: () => navigate('/billing/usage'),
    //     }]
    //   : []),
  ]

  // Calculate change type for stats
  const getChangeType = (change: number): 'positive' | 'negative' | 'neutral' => {
    if (change > 0) return 'positive'
    if (change < 0) return 'negative'
    return 'neutral'
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('overview.welcomeBack', { name: user?.name })}
        description={t('overview.subtitle', { organization: user?.tenantName || t('overview.organizationFallback') })}
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (error || !stats) ? (
        <ErrorState
          description={error || t('overview.failedToLoadData')}
          action={
            <LoadingButton onClick={() => window.location.reload()}>
              {t('common.retry')}
            </LoadingButton>
          }
        />
      ) : (
        <>
          {/* Alerts */}
          {alerts.length > 0 && (
            <AlertsPanel
              alerts={alerts}
              onAlertAction={(alert) => {
                if (alert.actionUrl) {
                  navigate(alert.actionUrl)
                }
              }}
            />
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              name={t('overview.currentPlan')}
              value={stats.currentPlan.tier ?? '—'}
              icon={CreditCard}
              change={undefined}
              // Cards stay visible for every role — the numbers are theirs to
              // read. Only the click-through drops, so a role that cannot open
              // billing does not get a card that looks clickable and isn't.
              onClick={showBilling ? () => navigate('/billing/subscription') : undefined}
            />
            <StatCard
              name={t('overview.teamMembers')}
              value={stats.teamMembers.total}
              icon={Users}
              change={stats.teamMembers.change > 0 ? `+${stats.teamMembers.change}` : `${stats.teamMembers.change}`}
              changeType={getChangeType(stats.teamMembers.change)}
              onClick={() => navigate('/people')}
            />
            {/* Seats, in the slot the API-usage card held. That card showed a
                Math.random() percentage that changed on every load; nothing
                meters API calls. Seats is measured and plan-aware, so the row
                stays four cards wide without inventing one. No `change` here:
                StatCard suffixes it with "from last month", which a
                point-in-time ratio is not. */}
            <StatCard
              name={t('overview.teamSeats')}
              value={seatsValue}
              icon={Gauge}
              change={undefined}
              onClick={showBilling ? () => navigate('/billing/subscription') : undefined}
            />
            <StatCard
              name={t('overview.activeFeatures')}
              value={stats.features.active}
              icon={Package}
              change={undefined}
              onClick={showBilling ? () => navigate('/billing/subscription') : undefined}
            />
          </div>

          {/* Quick Actions */}
          {quickActions.length > 0 && <QuickActions actions={quickActions} />}

          {/* Two Column Layout.

              UsageProgressBars used to sit on the left with three bars: API
              calls and storage were both random, and seats -- the only real one
              -- is now a card above. Rather than leave a panel with one bar
              duplicating that card, the chart moves up into the column. It
              renders its own "no data" state until metering exists, so this is
              one honest empty region instead of two. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <StorageUsageCard
                storage={stats.usage.storage}
                canBuy={showBilling}
                onUpgrade={() => navigate('/billing/subscription')}
              />
              <UsageChart data={usageTrends} title={t('overview.usageTrends')} />
            </div>

            <ActivityFeed
              activities={activities}
              onViewAll={() => navigate('/audit')}
            />
          </div>
        </>
      )}
    </div>
  )
}
